import throttle from '../util/throttle';
import {Collapsible} from '../features/collapsible';
import {a11y} from '../vendor/theme-scripts/theme-a11y';

const selectors = {
  drawer: '[data-drawer]',
  drawerToggle: '[data-drawer-toggle]',
  scroller: '[data-scroll]',
};
const classes = {
  open: 'is-open',
  drawerOpen: 'js-drawer-open',
};
const attributes = {
  ariaExpanded: 'aria-expanded',
  ariaControls: 'aria-controls',
};

class Drawer {
  constructor() {
    this.drawers = document.querySelectorAll(selectors.drawer);
    this.drawerToggleButtons = document.querySelectorAll(selectors.drawerToggle);
    this.a11y = a11y;

    this.drawerToggleEvent = throttle((e) => {
      this.toggle(e);
    }, 150);

    this.keyPressCloseEvent = throttle((e) => {
      if (e.keyCode === theme.keyboardKeys.ESCAPE) {
        this.close(e);
      }
    }, 150);

    // Define drawer close event
    this.drawerCloseEvent = (e) => {
      const activeDrawer = document.querySelector(`${selectors.drawer}.${classes.open}`);

      if (!activeDrawer) {
        return;
      }

      const isDrawerToggle = e.target.matches(selectors.drawerToggle);
      const isDrawerChild = activeDrawer ? activeDrawer.contains(e.target) : false;

      if (!isDrawerToggle && !isDrawerChild) {
        this.close(e);
      }
    };

    this.initListeners();
  }

  initListeners() {
    // Toggle event for each drawer button
    this.drawerToggleButtons.forEach((button) => {
      button.addEventListener('click', this.drawerToggleEvent);
    });

    // Close drawers if escape key pressed
    this.drawers.forEach((drawer) => {
      drawer.addEventListener('keyup', this.keyPressCloseEvent);

      // Init collapsible mobile dropdowns
      this.collapsible = new Collapsible(drawer);
    });

    // Close drawers on click outside
    document.addEventListener('click', this.drawerCloseEvent);
  }

  toggle(e) {
    e.preventDefault();
    const drawer = document.querySelector(`#${e.target.getAttribute(attributes.ariaControls)}`);
    if (!drawer) {
      return;
    }

    const isDrawerOpen = drawer.classList.contains(classes.open);

    if (isDrawerOpen) {
      this.close();
    } else {
      this.open(e);
    }
  }

  open(e) {
    const drawerOpenButton = e.target;
    const drawer = document.querySelector(`#${e.target.getAttribute(attributes.ariaControls)}`);

    if (!drawer) {
      return;
    }
    const drawerScroller = drawer.querySelector(selectors.scroller) || drawer;

    // Disable page scroll right away
    document.dispatchEvent(new CustomEvent('theme:scroll:lock', {bubbles: true, detail: drawerScroller}));
    document.dispatchEvent(new CustomEvent('theme:drawer:open'), {bubbles: true});
    document.body.classList.add(classes.drawerOpen);

    drawer.classList.add(classes.open);
    drawerOpenButton.setAttribute(attributes.ariaExpanded, true);

    setTimeout(() => {
      this.a11y.state.trigger = drawerOpenButton;
      this.a11y.trapFocus({
        container: drawer,
      });
    });
  }

  close() {
    if (!document.body.classList.contains(classes.drawerOpen)) {
      return;
    }

    const drawer = document.querySelector(`${selectors.drawer}.${classes.open}`);

    this.drawerToggleButtons.forEach((button) => {
      button.setAttribute(attributes.ariaExpanded, false);
    });

    // Enable page scroll right after the closing animation ends
    const timeout = 400;
    document.dispatchEvent(new CustomEvent('theme:scroll:unlock', {bubbles: true, detail: timeout}));

    this.a11y.removeTrapFocus({
      container: drawer,
    });

    drawer.classList.remove(classes.open);
    document.body.classList.remove(classes.drawerOpen);
    document.dispatchEvent(new CustomEvent('theme:drawer:close'), {bubbles: true});
  }

  unload() {
    // Close drawer
    this.close();

    // Unbind all event listeners for drawers
    this.drawerToggleButtons.forEach((button) => {
      button.removeEventListener('click', this.drawerToggleEvent);
    });
    this.drawers.forEach((drawer) => {
      drawer.removeEventListener('keyup', this.keyPressCloseEvent);
    });
    document.removeEventListener('click', this.drawerCloseEvent);

    if (this.collapsible) {
      this.collapsible.onUnload();
    }
  }
}

export default Drawer;
